// Core imports
import React, { useState } from 'react';

// Component imports
import { Badge, Col, Collapse, Empty, Input, Row, Typography } from 'antd';
import { QuestionCircleOutlined, SearchOutlined } from '@ant-design/icons';
import Highlighter from 'react-highlight-words';
import SEO from '../SEO/SEO';

// Misc imports
import NotFoundIcon from '../../assets/images/not-found.png';
import { SUPPORT_EMAIL } from '../../constants';

// Constant declarations
const { Title, Paragraph } = Typography;
const { Panel } = Collapse;

const faqs = [
  {
    id: 1,
    question: 'What is a near-death experience (NDE)?',
    answer: 'A near-death experience, or NDE, is a profound and often life-changing experience that some people report after coming close to death. The experience may include a feeling of leaving the body, moving through a tunnel or towards a bright light, meeting deceased loved ones, and experiencing a sense of peace, love, or unity. Not everyone who comes close to death reports having an NDE, and the exact cause and nature of the experience are still not fully understood by science.'
  },
  {
    id: 2,
    question: 'I submitted my NDE, but I have lost the link to edit it. How can I edit or delete my experience?',
    answer: 'If you are unable to locate the URL to edit or delete your NDE, please send an email to ' + SUPPORT_EMAIL + '. Your request will be handled promptly.'
  },
  {
    id: 3,
    question: 'I went out of my body during meditation/sleep, but I\'m not sure it qualifies as an NDE. Can I still submit my experience?',
    answer: 'Yes! While NDEs are the primary focus of this platform, we welcome a broad range of profound experiences. OBEs (Out-of-Body Experiences), STEs (Spiritually Transformative Experiences), and similar experiences are accepted. If your experience was meaningful and shares elements with NDEs, feel free to share it.'
  },
  {
    id: 4,
    question: 'What qualifies as clinical death?',
    answer: 'Clinical death occurs when a person\'s heartbeat, respiration, and blood circulation stop, typically as a result of severe trauma, illness, or injury. During clinical death, vital functions cease, and if not promptly reversed through medical intervention, it can lead to biological death.'
  },
  {
    id: 5,
    question: 'Are NDEs a natural phenomenon?',
    answer: 'Every phenomenon can be considered natural in some sense. Does current scientific knowledge fully explain NDEs? Not entirely. There are studies on the dying brain, particularly in rodents and, more recently, in humans. However, it is still unclear whether these studies fully explain NDEs, and further research is required before science can provide a comprehensive understanding of the phenomenon. On this website, we focus more on the content of the experiences rather than the underlying cause.'
  },
  {
    id: 6,
    question: 'Why create another platform for sharing NDEs when similar ones already exist?',
    answer: 'There are already other platforms for sharing NDEs, such as the wonderful IANDS and NDERF websites. However, the search and filter functionalities on these platforms are limited. Since the data shared on these sites is publicly available, NDERF data has been incorporated into this database in a more structured format. By applying the appropriate source filter, you can also find NDERF NDEs in the search results. Significant effort has been put into categorizing information according to the Greyson scale to make it easier for users and researchers to search and analyze NDEs (in accordance with Fair Use guidelines).'
  },
  {
    id: 7,
    question: 'How can I contribute my own NDE story?',
    answer: 'To share your own NDE, please visit the \'Share your experience\' page. You can submit your story through a simple form, which will then be reviewed before it\'s added to the database. Your anonymity is important, and you can share your experience without revealing any personally identifiable information.'
  },
  {
    id: 8,
    question: 'How do you ensure the authenticity of the NDEs on the platform?',
    answer: 'The platform relies on multiple sources, including NDERF, IANDS, and user contributions to collect NDE stories. While complete authenticity of every account cannot be guaranteed, efforts are made to verify the credibility of these experiences through various checks and by maintaining a high standard of quality in the database. Additionally, users are encouraged to critically evaluate the experiences and draw their own conclusions.'
  },
  {
    id: 9,
    question: 'Can I use the data on this platform for my research or project?',
    answer: 'Yes, you may use the data on this platform for research or projects, as long as you adhere to the Fair Use guidelines (link). Researchers and individuals are encouraged to explore the NDEs in the database and help advance the understanding of this phenomenon. When using the data, please ensure that you provide proper attribution to the platform and any other sources you\'ve utilized.'
  },
  {
    id: 10,
    question: 'How can I get involved or support the platform?',
    answer: `Your interest in supporting the platform is appreciated! There are several ways to get involved: by sharing your own NDE, providing feedback on the website, or helping spread the word about NDE Archive. If you have any suggestions, ideas, or would like to collaborate, please don't hesitate to reach out at ${SUPPORT_EMAIL}.`
  }
];

const iconCircleStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 72,
  height: 72,
  borderRadius: '50%',
  background: 'linear-gradient(135deg, rgba(145, 149, 252, 0.15), rgba(191, 193, 255, 0.15))',
  marginBottom: 20
};

const numberBadgeStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 28,
  height: 28,
  borderRadius: '50%',
  background: 'linear-gradient(135deg, rgb(145, 149, 252), rgb(191, 193, 255))',
  color: '#fff',
  fontSize: 13,
  fontWeight: 700,
  marginRight: 14,
  flexShrink: 0
};

const FaqScreen = () => {
  const [searchTerm, setSearchTerm] = useState('');

  const onSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const filteredFaqs = faqs.filter((faq) => {
    return faq.question.toLowerCase().includes(searchTerm.toLowerCase()) || faq.answer.toLowerCase().includes(searchTerm.toLowerCase());
  });

  const faqJsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqs.map((faq) => ({
        '@type': 'Question',
        name: faq.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: faq.answer
        }
      }))
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: process.env.REACT_APP_SITE_URL || 'https://ndearchive.com' },
        { '@type': 'ListItem', position: 2, name: 'FAQ', item: `${process.env.REACT_APP_SITE_URL || 'https://ndearchive.com'}/faq` }
      ]
    }
  ];

  const headerSection = (
    <div className="text-center mb-32" style={{ marginTop: 16 }}>
      <div style={iconCircleStyle}>
        <QuestionCircleOutlined className="text-primary" style={{ fontSize: 36 }} />
      </div>
      <Title level={1} style={{ color: '#1a1a2e', fontWeight: 800, letterSpacing: '-0.5px', marginBottom: 8 }}>Frequently Asked Questions</Title>
      <Paragraph style={{ color: '#555', fontSize: 16, maxWidth: 560, margin: '0 auto' }}>
        Find answers to the most common questions about near-death experiences and how the platform works.
      </Paragraph>
    </div>
  );

  const searchInput = (
    <Input
      placeholder="Search FAQs..."
      size="large"
      prefix={<SearchOutlined style={{ color: '#b2bec3' }} />}
      onChange={onSearch}
      className="mb-24"
      allowClear
      style={{ borderRadius: 12, height: 48, boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}
      suffix={
        <Badge
          count={filteredFaqs.length}
          style={{ backgroundColor: 'rgb(145, 149, 252)' }}
        />
      }
    />
  );

  if (filteredFaqs.length === 0) {
    return (
      <>
        <SEO
          title="FAQ | Near-Death Experiences"
          description="Frequently asked questions about near-death experiences (NDEs), the NDE Archive platform, and how to contribute your own experience."
          path="/faq"
          jsonLd={faqJsonLd}
        />
        <Row justify="center">
          <Col xs={24} md={20} lg={16}>
            {headerSection}
            {searchInput}
            <Empty
              description={<span style={{ color: '#888', fontSize: 15 }}>No matching questions found. Try a different search term.</span>}
              image={NotFoundIcon}
              imageStyle={{ height: 200 }}
              className="mb-24"
            />
          </Col>
        </Row>
      </>
    );
  }

  return (
    <>
      <SEO
        title="FAQ | Near-Death Experiences"
        description="Frequently asked questions about near-death experiences (NDEs), the NDE Archive platform, and how to contribute your own experience."
        path="/faq"
        jsonLd={faqJsonLd}
      />
      <Row justify="center" className="mb-64">
        <Col xs={24} md={20} lg={16}>
          {headerSection}
          {searchInput}

          <Collapse
            accordion
            bordered={false}
            className="faq-collapse"
            expandIconPosition="end"
          >
            {
              filteredFaqs.map((faq, index) => (
                <Panel
                  header={
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <span style={numberBadgeStyle}>{faq.id}</span>
                      <Highlighter
                        searchWords={[searchTerm]}
                        autoEscape={true}
                        textToHighlight={faq.question}
                        highlightClassName="highlight"
                        className="faq-question"
                      />
                    </div>
                  }
                  key={index}
                >
                  <Highlighter
                    searchWords={[searchTerm]}
                    autoEscape={true}
                    textToHighlight={faq.answer}
                    highlightClassName="highlight"
                    className="faq-answer"
                  />
                </Panel>
              ))}
          </Collapse>
        </Col>
      </Row>
    </>
  );
};

export default FaqScreen;
